import type { ConfiguratorCustomization } from "../../contract/context";

export type ConfiguratorSummaryActionId =
  | "requestQuotation"
  | "requestOffer"
  | "saveConfiguration"
  | "downloadDatasheet"
  | "addToCart"
  | "addToQuote"
  | "goToCart"
  | "contactSpecialist";

export interface SummaryAudienceCapabilities {
  audience: "public" | "registered" | "customer";
  canAdd: boolean;
  canPurchase: boolean;
  canSeePrice: boolean;
  canSeeAvailability: boolean;
}

export interface SummaryCapabilityInput {
  customization: ConfiguratorCustomization;
  audience: SummaryAudienceCapabilities;
  configurationComplete: boolean;
  commerceSettled: boolean;
  commerceSubmitted: boolean;
  configurationMutationPending: boolean;
  webservicesDisabled: boolean;
}

export interface SummaryActionCapability {
  id: ConfiguratorSummaryActionId;
  visible: boolean;
  enabled: boolean;
}

export interface SummaryCapabilities {
  showPrice: boolean;
  showAvailability: boolean;
  showCad: boolean;
  showPriceReference: boolean;
  showQuantity: boolean;
  priceReferenceEditable: boolean;
  actions: readonly SummaryActionCapability[];
}

export const buildSummaryCapabilities = ({
  customization,
  audience,
  configurationComplete,
  commerceSettled,
  commerceSubmitted,
  configurationMutationPending,
  webservicesDisabled,
}: SummaryCapabilityInput): SummaryCapabilities => {
  const isPublicOrRegistered = audience.audience === "public" || audience.audience === "registered";
  const actionable = configurationComplete && !configurationMutationPending;
  const commercialActionable = actionable && commerceSettled;
  const showAddAction = audience.audience === "customer" && audience.canAdd && !commerceSubmitted;

  return {
    showPrice: audience.audience === "customer" && customization.showPrice && audience.canSeePrice,
    showAvailability:
      audience.audience === "customer" &&
      customization.showAvailability &&
      audience.canSeeAvailability &&
      audience.canPurchase,
    showCad: customization.forceShowCAD || !customization.embedded,
    showPriceReference: audience.audience === "customer",
    showQuantity: audience.audience === "customer" || (isPublicOrRegistered && customization.showRequestQuotation),
    priceReferenceEditable: audience.audience === "customer",
    actions: [
      {
        id: "requestQuotation",
        visible: isPublicOrRegistered && customization.showRequestQuotation,
        enabled: commercialActionable && !webservicesDisabled,
      },
      {
        id: "requestOffer",
        visible: isPublicOrRegistered && !customization.embedded,
        enabled: actionable,
      },
      {
        id: "saveConfiguration",
        visible: customization.showSave,
        enabled: actionable,
      },
      {
        id: "downloadDatasheet",
        visible: isPublicOrRegistered && customization.showDownloadPDF,
        enabled: actionable && !webservicesDisabled,
      },
      {
        id: "addToCart",
        visible: showAddAction && audience.canPurchase,
        enabled: commercialActionable && !webservicesDisabled,
      },
      {
        id: "addToQuote",
        visible: showAddAction && !audience.canPurchase,
        enabled: commercialActionable && !webservicesDisabled,
      },
      {
        id: "goToCart",
        visible: audience.audience === "customer" && audience.canAdd && commerceSubmitted,
        enabled: actionable && !webservicesDisabled,
      },
      {
        id: "contactSpecialist",
        visible: customization.showContactSpecialistLink,
        enabled: actionable,
      },
    ],
  };
};
